import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AddIcon from '@atlaskit/icon/core/add';
import { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { type ContentClassificationMetadata } from '@confluence/data-classification/entry-points/useContentClassification';
import { useClassificationModalDispatchContext } from '@confluence/data-classification/entry-points/ClassificationModalContext';
import { ClassificationEditModal } from '@confluence/data-classification/entry-points/ClassificationEditModal';
import { usePreloadClassificationEditorQueries } from '@confluence/data-classification/entry-points/hooks';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';

const i18n = defineMessages({
	addClassificationLabel: {
		id: 'details-panel.activity-section.classification-row.add-classification-label',
		defaultMessage: 'Add classification',
		description:
			'aria-label for button to add classification (highly confidential, confidential, internal, public) to content',
	},
});

export type AddClassificationProps = {
	contentClassificationMetadata: ContentClassificationMetadata;
	hasEditPermission: boolean;
};

export const AddClassification = ({
	contentClassificationMetadata,
	hasEditPermission,
}: AddClassificationProps) => {
	const { formatMessage } = useIntl();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();

	const { setIsClassificationModalOpen } = useClassificationModalDispatchContext();
	const preloadClassificationEditorQueries = usePreloadClassificationEditorQueries({ spaceKey });

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onClick = () => {
		setIsClassificationModalOpen(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: `button`,
				actionSubjectId: 'SidePanelClassification',
				source: 'contentWrapper',
			},
		}).fire();
	};

	return hasEditPermission ? (
		<>
			<IconButton
				appearance="subtle"
				icon={AddIcon}
				label={formatMessage(i18n.addClassificationLabel)}
				onClick={onClick}
				onMouseEnter={preloadClassificationEditorQueries}
			/>
			<ClassificationEditModal
				contentId={contentId ?? ''}
				spaceKey={spaceKey ?? ''}
				contentClassificationMetadata={contentClassificationMetadata}
			/>
		</>
	) : null;
};
